<template>
  <div class="inspection-progress" data-testid="inspection-progress">
    <div class="inspection-progress__header">
      <span class="inspection-progress__title">Inspection progress</span>
      <span class="inspection-progress__total">{{ totalLabel }}</span>
    </div>
    <div class="inspection-progress__bar" role="img" :aria-label="summary">
      <span
        v-for="(state, index) in normalizedStates"
        :key="index"
        class="inspection-progress__segment"
        :class="`inspection-progress__segment--${state}`"
        data-testid="inspection-progress-segment"
      />
    </div>
    <p class="inspection-progress__summary" data-testid="inspection-progress-summary">{{ summary }}</p>
  </div>
</template>

<script setup>
import { computed } from "vue";

const props = defineProps({
  /** One entry per protocol step: "passed", "failed" or "not_yet_inspected". */
  states: {
    type: Array,
    default: () => [],
  },
});

const normalizedStates = computed(() =>
  props.states.map((state) => (state === "passed" || state === "failed" ? state : "not_yet_inspected"))
);

const counts = computed(() => ({
  passed: normalizedStates.value.filter((state) => state === "passed").length,
  failed: normalizedStates.value.filter((state) => state === "failed").length,
  notYetInspected: normalizedStates.value.filter((state) => state === "not_yet_inspected").length,
}));

const totalLabel = computed(() => {
  const total = normalizedStates.value.length;
  return `${total} ${total === 1 ? "step" : "steps"}`;
});

const summary = computed(
  () =>
    `${counts.value.passed} passed, ${counts.value.failed} failed, ${counts.value.notYetInspected} not yet inspected`
);
</script>

<style scoped>
.inspection-progress {
  display: flex;
  flex-direction: column;
  gap: 8px;
  font-family: Inter, sans-serif;
}

.inspection-progress__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.inspection-progress__title {
  color: #344054;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
}

.inspection-progress__total {
  color: #667085;
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
}

.inspection-progress__bar {
  display: flex;
  gap: 4px;
  height: 8px;
}

.inspection-progress__segment {
  flex: 1 1 0;
  min-width: 0;
  border-radius: 4px;
}

.inspection-progress__segment--passed {
  background: #17b26a;
}

.inspection-progress__segment--failed {
  background: #f04438;
}

.inspection-progress__segment--not_yet_inspected {
  background: #d0d5dd;
}

.inspection-progress__summary {
  margin: 0;
  color: #475467;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
}
</style>
